/**
 * Shared view helpers for the opportunity radar (lens + matrix): human topic
 * labels, "is this topic active" (worth surfacing), and an activity sort. Keeps
 * the two panels consistent and avoids dumping dozens of identical baseline rows.
 */
import { TOPIC_REGISTRY } from "@regpulse/contracts";

import type { OpportunityRadarEntry } from "../services/api";

const LABELS = new Map<string, { zh: string; en: string }>(
  TOPIC_REGISTRY.map((t) => [t.id, { zh: t.labelZh, en: t.labelEn }]),
);

/** Human label for a topic id (falls back to the id for unknown/test ids). */
export function topicLabel(topicId: string): { zh: string; en: string } {
  return LABELS.get(topicId) ?? { zh: topicId, en: topicId };
}

export const TYPE_RANK: Record<string, number> = { risk: 0, opportunity: 1, watch: 2, none: 3 };

/**
 * A topic worth surfacing: a risk/opportunity verdict, real market pain, or an
 * active draft trajectory. Baseline topics (law only, no signals) are not.
 *
 * NOTE: "watch" is deliberately NOT active on its own. With seed law present,
 * the mock evaluator scores almost every topic "watch", so including it would
 * dump all ~48 baseline rows back into the default view — the exact clutter the
 * active-only filter exists to remove. A watch topic still surfaces once it has
 * real pain (med/high) or a draft direction, or via the "显示全部" toggle.
 */
export function isActiveTopic(r: OpportunityRadarEntry): boolean {
  return (
    r.opportunityType === "risk" ||
    r.opportunityType === "opportunity" ||
    r.intensity === "med" ||
    r.intensity === "high" ||
    (r.direction !== undefined && r.direction !== "none" && r.direction !== "unclear")
  );
}

/** Sort comparator: active topics first, then by verdict severity. */
export function byActivity(a: OpportunityRadarEntry, b: OpportunityRadarEntry): number {
  const aActive = isActiveTopic(a) ? 0 : 1;
  const bActive = isActiveTopic(b) ? 0 : 1;
  if (aActive !== bActive) return aActive - bActive;
  return (TYPE_RANK[a.opportunityType] ?? 9) - (TYPE_RANK[b.opportunityType] ?? 9);
}

// ─── Content-first helpers (information redesign) ────────────────────────────
// The categorical labels are near-uniform across topics (strict law everywhere,
// honest-mock verdicts all "watch"), so ranking/encoding by them carries no
// information. These helpers rank and annotate by the CONTENT that varies:
// draft deadlines/recency, counts, country tags, pain quotes.

/** A topic with real signal content (drafts or pain) — the briefing's rows. */
export function hasContent(r: OpportunityRadarEntry): boolean {
  return (r.draftCount ?? 0) > 0 || (r.intelCount ?? 0) > 0;
}

/**
 * "What moved" comparator: nearest FUTURE deadline first (standstill/effective
 * dates are the only hard clocks in the data), then most-recently-fetched draft,
 * then draft volume, then pain volume.
 */
export function byWhatMoved(
  nowIso: string,
): (a: OpportunityRadarEntry, b: OpportunityRadarEntry) => number {
  const deadlineKey = (r: OpportunityRadarEntry): string => {
    const d = r.latestDraft?.effectiveDate;
    return d && d >= nowIso.slice(0, 10) ? d : "9999-12-31";
  };
  return (a, b) => {
    const dl = deadlineKey(a).localeCompare(deadlineKey(b));
    if (dl !== 0) return dl;
    const fetched = (b.latestDraft?.fetchedAt ?? "").localeCompare(a.latestDraft?.fetchedAt ?? "");
    if (fetched !== 0) return fetched;
    const drafts = (b.draftCount ?? 0) - (a.draftCount ?? 0);
    if (drafts !== 0) return drafts;
    return (b.intelCount ?? 0) - (a.intelCount ?? 0);
  };
}

/** Days from `nowIso` (date part) until `iso`; negative = past. */
export function daysUntil(iso: string, nowIso: string): number {
  const ms = Date.parse(iso) - Date.parse(nowIso.slice(0, 10));
  return Math.round(ms / 86_400_000);
}

/** The entry with the nearest FUTURE latestDraft.effectiveDate, or null. */
export function nearestDeadline(
  entries: OpportunityRadarEntry[],
  nowIso: string,
): { entry: OpportunityRadarEntry; date: string } | null {
  let best: { entry: OpportunityRadarEntry; date: string } | null = null;
  const today = nowIso.slice(0, 10);
  for (const r of entries) {
    const d = r.latestDraft?.effectiveDate;
    if (!d || d < today) continue;
    if (best === null || d < best.date) best = { entry: r, date: d };
  }
  return best;
}

/** TRIS national drafts carry a "[FR] …" country prefix in the title — split it
 *  out so the country renders as a chip and the headline stays clean. */
export function parseCountryTag(title: string): { country?: string; headline: string } {
  const m = title.match(/^\[([A-Z]{2})\]\s*(.+)$/);
  return m ? { country: m[1], headline: m[2]! } : { headline: title };
}

// Stable per-domain hue (the map's bubble fill — verdict color carried zero
// bits when everything is "watch", and verdict-colored marks flirt with
// implying verdicts, ADR-0002).
const DOMAIN_PALETTE = [
  "#5b8def", "#30a46c", "#d9a200", "#e5484d", "#9d6cff",
  "#2bb8c4", "#e0759a", "#8f9a4a", "#c97f3d", "#7a8a99",
] as const;
export function domainColor(domainId: string): string {
  let h = 0;
  for (let i = 0; i < domainId.length; i++) h = (h * 31 + domainId.charCodeAt(i)) >>> 0;
  return DOMAIN_PALETTE[h % DOMAIN_PALETTE.length]!;
}

// ─── Diagnosis labels (RFC-0008 §2.3) ────────────────────────────────────────
// The content-anchored 机会/风险 subtype the P1 live analysis emits. The
// compliance_headroom note is part of the model: ignoring an already-permitted
// fix converts the opportunity into a competitive risk.
export const DIAGNOSIS_LABEL: Record<string, { zh: string; note?: string }> = {
  regulatory_squeeze: { zh: "监管夹击 · 市场诉求与强制底线冲突" },
  riding_loosening: { zh: "借势松绑 · 痛点对应的强制要求正被草案放宽" },
  compliance_headroom: {
    zh: "合规盲区 · 法规已允许可解此痛点的方案",
    note: "忽视则成竞争风险",
  },
  watch: { zh: "观察 · 痛点与法规空间暂无明确关系" },
  none: { zh: "暂无信号" },
};

// ─── Bubble-chart scoring (audit G8) ─────────────────────────────────────────
// Collapse the three categorical axes into the two continuous dimensions the
// opportunity map plots: X = regulatory pressure (current-law strictness + draft
// trajectory), Y = market-pain intensity. Bubble size = signal volume. All on a
// 0–3 scale with a 1.5 midpoint, so the four quadrants carry meaning.

export const AXIS_MAX = 3;
export const AXIS_MID = 1.5;

/** X axis: how much regulatory pressure sits on this topic (0–3). */
export function regulatoryPressure(r: OpportunityRadarEntry): number {
  const law =
    r.strictness === "strict" ? 2 : r.strictness === "moderate" ? 1.2 : r.strictness === "light" ? 0.5 : 0;
  const draft =
    r.direction === "tightening"
      ? 1
      : r.direction === "new"
        ? 0.8
        : r.direction === "loosening"
          ? 0.4
          : r.direction === "unclear"
            ? 0.3
            : 0;
  return Math.min(AXIS_MAX, law + draft);
}

/** Y axis: market-pain intensity for this topic (0–3). */
export function painScore(r: OpportunityRadarEntry): number {
  return r.intensity === "high" ? 3 : r.intensity === "med" ? 2 : r.intensity === "low" ? 1 : 0;
}

/** Bubble size proxy: total advisory signals (market-voice + draft) on the topic. */
export function signalVolume(r: OpportunityRadarEntry): number {
  return (r.intelCount ?? 0) + (r.draftCount ?? 0);
}

export type Quadrant = "act_now" | "blue_ocean" | "table_stakes" | "low_priority";

export interface QuadrantMeta {
  key: Quadrant;
  zh: string;
  hint: string;
}

const QUADRANTS: Record<Quadrant, QuadrantMeta> = {
  act_now: { key: "act_now", zh: "红海 · 合规风险", hint: "高痛点 + 强监管：尽快应对" },
  blue_ocean: { key: "blue_ocean", zh: "蓝海 · 差异化机会", hint: "高痛点 + 弱监管：产品先行" },
  table_stakes: { key: "table_stakes", zh: "合规底线", hint: "低痛点 + 强监管：达标即可" },
  low_priority: { key: "low_priority", zh: "低优先 · 观察", hint: "低痛点 + 弱监管：持续观察" },
};

/** Which quadrant a topic falls into, given its pressure × pain coordinates. */
export function quadrantOf(r: OpportunityRadarEntry): QuadrantMeta {
  const highPain = painScore(r) >= AXIS_MID;
  const highReg = regulatoryPressure(r) >= AXIS_MID;
  if (highPain && highReg) return QUADRANTS.act_now;
  if (highPain && !highReg) return QUADRANTS.blue_ocean;
  if (!highPain && highReg) return QUADRANTS.table_stakes;
  return QUADRANTS.low_priority;
}

// ─── Bubble collision avoidance (audit G8) ───────────────────────────────────
// The three axes are categorical, so many topics share an exact (x,y) — without
// spreading, 15 bubbles pile onto one point and labels turn to mush. This is a
// deterministic relaxation: seed coincident points with an index-based spiral
// nudge, then push overlapping pairs apart (clamped to the plot box). Pure +
// deterministic (no Math.random) so the layout is stable and testable.

export interface PackPoint {
  id: string;
  x: number;
  y: number;
  r: number;
}
export interface PackBounds {
  x0: number;
  y0: number;
  x1: number;
  y1: number;
}

const GOLDEN_ANGLE = 2.399963229728653;

/**
 * Spread bubbles so none overlap, keeping each near its true position and inside
 * `bounds`. Returns new points (input not mutated). `gap` is extra px between
 * circle edges; `iterations` bounds the relaxation cost (O(n² · iterations)).
 */
export function resolveBubbleCollisions(
  points: PackPoint[],
  bounds: PackBounds,
  iterations = 90,
  gap = 4,
): PackPoint[] {
  // Index-based spiral seed so points that start coincident get distinct spots.
  const pts = points.map((p, i) => ({
    ...p,
    x: p.x + Math.cos(i * GOLDEN_ANGLE) * 0.05 * (i + 1),
    y: p.y + Math.sin(i * GOLDEN_ANGLE) * 0.05 * (i + 1),
  }));
  for (let it = 0; it < iterations; it++) {
    for (let i = 0; i < pts.length; i++) {
      const a = pts[i];
      if (!a) continue;
      for (let j = i + 1; j < pts.length; j++) {
        const b = pts[j];
        if (!b) continue;
        let dx = b.x - a.x;
        let dy = b.y - a.y;
        let d = Math.hypot(dx, dy);
        const min = a.r + b.r + gap;
        if (d < min) {
          if (d < 1e-6) {
            // Perfectly coincident: pick a deterministic direction by index.
            dx = Math.cos((i + 1) * GOLDEN_ANGLE);
            dy = Math.sin((i + 1) * GOLDEN_ANGLE);
            d = 1;
          }
          const push = (min - d) / 2;
          const ux = dx / d;
          const uy = dy / d;
          a.x -= ux * push;
          a.y -= uy * push;
          b.x += ux * push;
          b.y += uy * push;
        }
      }
    }
    for (const p of pts) {
      p.x = Math.min(bounds.x1 - p.r, Math.max(bounds.x0 + p.r, p.x));
      p.y = Math.min(bounds.y1 - p.r, Math.max(bounds.y0 + p.r, p.y));
    }
  }
  return pts;
}
